import React, { useState, useEffect } from "react";
import { formAPI } from "../../../../../services/api";
import "./11DBmanage.css";
import { useNavigate } from "react-router-dom";
import formFieldKeys from "./11formFields";

const VIP11DBmanage = ({ projectId }) => {
  const [userValue, setUserValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // 유저 관리 상태
  const [activeTab, setActiveTab] = useState("download"); // "download" | "users" | "delete"
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState({});
  const [newPasswords, setNewPasswords] = useState({});
  const [confirmPasswords, setConfirmPasswords] = useState({});

  // 데이터 삭제 관련 상태
  const [deleteData, setDeleteData] = useState({
    hosId: "",
    dataId: "",
    confirmText: "",
  });

  // 예약된 삭제 작업 관리
  const [scheduledDeletions, setScheduledDeletions] = useState([]);
  const [deletionInProgress, setDeletionInProgress] = useState(false);

  // user_value 조회
  useEffect(() => {
    if (!projectId) return;

    (async () => {
      try {
        const res = await formAPI.getUserValue(projectId);
        const val = res.userValue ?? res.user_value ?? null;
        setUserValue(val);
      } catch (err) {
        console.error("사용자 정보 조회 실패:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  // 유저 목록 조회
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetch(
        "https://product.idea2m.com/api/vip11/users",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `API 호출 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      if (result.success) {
        setUsers(result.data);
        console.log(`${result.count}명의 유저 조회 완료`);

        // 디버깅: 첫 번째 유저 데이터 확인
        if (result.data.length > 0) {
          console.log("첫 번째 유저 데이터:", result.data[0]);
          console.log("current_password 값:", result.data[0].current_password);
        }
      } else {
        throw new Error(result.message || "유저 조회 실패");
      }
    } catch (error) {
      console.error("유저 조회 실패:", error);
      alert(`유저 조회 실패: ${error.message}`);
    } finally {
      setUsersLoading(false);
    }
  };

  // 패스워드 유효성 검증
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "패스워드는 최소 8자 이상이어야 합니다.";
    }
    if (!hasUpperCase) {
      return "패스워드에 대문자가 포함되어야 합니다.";
    }
    if (!hasLowerCase) {
      return "패스워드에 소문자가 포함되어야 합니다.";
    }
    if (!hasNumbers) {
      return "패스워드에 숫자가 포함되어야 합니다.";
    }
    if (!hasSpecialChar) {
      return '패스워드에 특수문자가 포함되어야 합니다. (!@#$%^&*(),.?":{}|<>)';
    }
    return null; // 유효함
  };

  // 패스워드 변경
  const handlePasswordChange = async (userId) => {
    const newPassword = newPasswords[userId];
    const confirmPassword = confirmPasswords[userId];

    if (!newPassword || newPassword.trim() === "") {
      alert("새 패스워드를 입력해주세요.");
      return;
    }

    if (!confirmPassword || confirmPassword.trim() === "") {
      alert("패스워드 확인을 입력해주세요.");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("새 패스워드와 패스워드 확인이 일치하지 않습니다.");
      return;
    }

    // 패스워드 유효성 검증
    const validationError = validatePassword(newPassword);
    if (validationError) {
      alert(validationError);
      return;
    }

    setPasswordChanging((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await fetch(
        `https://product.idea2m.com/api/vip11/users/${userId}/password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ newPassword }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(
          `${result.data.username} 사용자의 패스워드가 성공적으로 변경되었습니다.`
        );
        setNewPasswords((prev) => ({ ...prev, [userId]: "" }));
        setConfirmPasswords((prev) => ({ ...prev, [userId]: "" }));
      } else {
        throw new Error(result.message || "패스워드 변경 실패");
      }
    } catch (error) {
      console.error("패스워드 변경 실패:", error);
      alert(`패스워드 변경 실패: ${error.message}`);
    } finally {
      setPasswordChanging((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab === "users" && userValue === 999) {
      fetchUsers();
    } else if (activeTab === "delete" && userValue === 999) {
      fetchScheduledDeletions();
    }
  }, [activeTab, userValue]);

  // 공통 다운로드 핸들러
  const handleDownload = async (format = "csv") => {
    setDownloading(true);
    try {
      const formatName = format.toUpperCase();
      console.log(`VIP11 ${formatName} 다운로드 시작...`);

      // 배포 서버 호출
      const downloadUrl = `https://product.idea2m.com/api/vip11/export?format=${format}`;

      console.log("다운로드 URL:", downloadUrl);
      console.log("요청 시작 시간:", new Date().toISOString());

      const acceptHeader =
        format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*"
          : "text/csv, application/csv, */*";

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Accept: acceptHeader,
        },
        mode: "cors", // CORS 명시적 설정
        credentials: "omit", // 인증 정보 제외
      });

      console.log("응답 상태:", response.status, response.statusText);
      console.log("응답 헤더:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API 응답 에러:", errorText);
        throw new Error(
          `API 호출 실패: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      console.log("응답 성공, Blob 생성 중...");

      // 파일 다운로드 처리
      const blob = await response.blob();
      console.log("Blob 크기:", blob.size, "바이트");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // 파일명 추출 (Content-Disposition 헤더에서)
      const contentDisposition = response.headers.get("Content-Disposition");
      const defaultExtension = format === "excel" ? "xlsx" : "csv";
      let filename = `vip11_export.${defaultExtension}`;

      if (contentDisposition) {
        console.log("Content-Disposition:", contentDisposition);
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      console.log("다운로드 파일명:", filename);

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`${formatName} 파일 다운로드 완료:`, filename);
    } catch (error) {
      console.error(`${format.toUpperCase()} 다운로드 실패:`, error);
      console.error("에러 스택:", error.stack);

      // 더 자세한 에러 정보 표시
      let errorMessage = "다운로드 실패";
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorMessage = "서버 연결 실패. 네트워크 상태를 확인해주세요.";
      } else if (error.message.includes("CORS")) {
        errorMessage =
          "CORS 정책으로 인한 요청 차단. 서버 설정을 확인해주세요.";
      } else {
        errorMessage = `다운로드 실패: ${error.message}`;
      }

      alert(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // 예약 삭제 작업 시작 (24시간 후 삭제)
  const handleDeleteData = async () => {
    const { hosId, dataId } = deleteData;

    setDeletionInProgress(true);

    try {
      console.log(
        `[11DBmanage] 예약 삭제 API 호출: hos_id=${hosId}, data_id=${dataId}`
      );

      // 예약 삭제 API 호출
      const response = await fetch(
        "https://product.idea2m.com/api/vip11/schedule-delete",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            hosId: hosId,
            dataId: dataId,
          }),
        }
      );

      console.log(
        "[11DBmanage] 응답 상태:",
        response.status,
        response.statusText
      );
      console.log(
        "[11DBmanage] 응답 헤더:",
        Object.fromEntries(response.headers.entries())
      );

      // 응답이 JSON인지 확인
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error(
          "[11DBmanage] 비JSON 응답:",
          textResponse.substring(0, 200)
        );
        throw new Error(
          `서버가 HTML을 반환했습니다. API 엔드포인트를 확인하세요. Status: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.success) {
        console.log("[11DBmanage] 예약 삭제 성공:", result);

        const scheduledTime = new Date(result.scheduledDeletion.scheduled_for);
        const hoursRemaining = Math.round(
          (scheduledTime - new Date()) / (1000 * 60 * 60)
        );

        alert(
          `삭제 예약 완료!\n\nhos_id: ${hosId}\ndata_id: ${dataId}\n\n예정 시간: ${scheduledTime.toLocaleString(
            "ko-KR",
            { timeZone: "Asia/Seoul" }
          )}\n남은 시간: 약 ${hoursRemaining}시간\n\n예약된 삭제는 언제든 취소할 수 있습니다.`
        );

        // 입력 필드 초기화
        setDeleteData({ hosId: "", dataId: "", confirmText: "" });

        // 예약된 삭제 목록 새로고침
        fetchScheduledDeletions();
      } else {
        throw new Error(result.message || "예약 삭제 실패");
      }
    } catch (error) {
      console.error("[11DBmanage] 예약 삭제 실패:", error);

      // 에러 메시지 표시
      let errorMessage = "예약 삭제 실패";
      if (error.message.includes("404")) {
        errorMessage = "해당 데이터를 찾을 수 없습니다.";
      } else if (error.message.includes("409")) {
        errorMessage = "이미 삭제가 예약된 데이터입니다.";
      } else if (error.message.includes("네트워크")) {
        errorMessage = "서버 연결 실패. 서버가 실행 중인지 확인해주세요.";
      } else {
        errorMessage = `예약 삭제 실패: ${error.message}`;
      }

      alert(errorMessage);
    } finally {
      setDeletionInProgress(false);
    }
  };

  // 예약된 삭제 목록 조회
  const fetchScheduledDeletions = async () => {
    try {
      const response = await fetch(
        "https://product.idea2m.com/api/vip11/scheduled-deletions",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setScheduledDeletions(result.data);
        console.log(`[11DBmanage] ${result.count}개의 예약된 삭제 조회 완료`);
      }
    } catch (error) {
      console.error("[11DBmanage] 예약된 삭제 목록 조회 실패:", error);
    }
  };

  // 예약 삭제 취소
  const handleCancelScheduledDeletion = async (scheduleId, hosId, dataId) => {
    try {
      console.log(
        `[11DBmanage] 예약 취소 요청: scheduleId=${scheduleId}, hosId=${hosId}, dataId=${dataId}`
      );

      const response = await fetch(
        `https://product.idea2m.com/api/vip11/cancel-scheduled-delete/${scheduleId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        alert(`예약 삭제 취소 완료!\nhos_id: ${hosId}\ndata_id: ${dataId}`);
        fetchScheduledDeletions(); // 목록 새로고침
      } else {
        throw new Error(result.message || "취소 실패");
      }
    } catch (error) {
      console.error("[11DBmanage] 예약 삭제 취소 실패:", error);
      alert(`예약 삭제 취소 실패: ${error.message}`);
    }
  };

  // 삭제 작업 취소
  const handleCancelDeletion = (deletionId) => {
    setPendingDeletions((prev) =>
      prev.map((deletion) =>
        deletion.id === deletionId
          ? { ...deletion, status: "취소됨", progress: 0 }
          : deletion
      )
    );

    // 취소된 항목은 2초 후 제거
    setTimeout(() => {
      setPendingDeletions((prev) => prev.filter((d) => d.id !== deletionId));
    }, 2000);
  };

  // 접근 제한 메시지 컴포넌트
  const AccessDeniedMessage = () => (
    <div
      style={{
        padding: "2rem",
        textAlign: "center",
        backgroundColor: "#fff3f3",
        borderRadius: "8px",
        margin: "2rem",
      }}
    >
      <h2 style={{ color: "#dc3545", marginBottom: "1rem" }}>접근 권한 없음</h2>
      <p style={{ color: "#666" }}>
        이 페이지는 관리자 전용 페이지입니다.
        <br />
        프로젝트 이용자는 DB에 직접적인 접근이 불가합니다.
      </p>
    </div>
  );

  if (loading) {
    return <div>로딩 중...</div>;
  }

  // 관리자가 아닌 경우 접근 제한 메시지 표시
  if (userValue !== 999) {
    return <AccessDeniedMessage />;
  }

  return (
    <div className="vip-dbmanage-container">
      <div className="dbmanage-header">
        <h1>DB 관리</h1>
        <p className="description">
          데이터 다운로드 및 유저 관리 기능을 제공합니다.
        </p>

        {/* 탭 네비게이션 */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === "download" ? "active" : ""}`}
            onClick={() => setActiveTab("download")}
          >
            데이터 다운로드
          </button>
          <button
            className={`tab-button ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            유저 패스워드 확인
          </button>

          <button
            className={`tab-button ${activeTab === "delete" ? "active" : ""}`}
            onClick={() => setActiveTab("delete")}
          >
            데이터 삭제 및 복구
          </button>
        </div>
      </div>

      {/* 탭 내용 */}
      {activeTab === "download" && (
        <>
          <div className="download-options">
            <div className="download-card">
              <h3>CSV 형식 (.csv)</h3>
              <p>모든 데이터를 CSV 형식으로 다운로드합니다.</p>
              <button
                className="download-button csv"
                onClick={() => handleDownload("csv")}
                disabled={downloading}
              >
                {downloading ? "다운로드 중..." : "CSV 다운로드"}
              </button>
            </div>

            <div className="download-card">
              <h3>Excel 형식 (.xlsx)</h3>
              <p>모든 데이터를 Excel 형식으로 다운로드합니다.</p>
              <button
                className="download-button excel"
                onClick={() => handleDownload("excel")}
                disabled={downloading}
              >
                {downloading ? "다운로드 중..." : "Excel 다운로드"}
              </button>
            </div>
          </div>

          <div className="form-keys-section">
            <h3>포함되는 데이터 필드</h3>
            <p className="form-keys-info">
              포함되는 필드 수: {formFieldKeys.length}개
            </p>
            <div className="form-keys-list">
              {formFieldKeys.map((key, index) => (
                <div key={index} className="form-key-item">
                  <span className="key">{key}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === "users" && (
        <div className="users-management-section">
          <div className="users-header">
            <h3>유저 패스워드 관리</h3>
            <p>프로젝트 유저들의 패스워드를 변경할 수 있습니다.</p>
            <div className="password-requirements">
              <h4>패스워드 조건:</h4>
              <ul>
                <li>최소 8자 이상</li>
                <li>대문자 포함 (A-Z)</li>
                <li>소문자 포함 (a-z)</li>
                <li>숫자 포함 (0-9)</li>
                <li>특수문자 포함 (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
              </ul>
            </div>
            <button
              className="refresh-button"
              onClick={fetchUsers}
              disabled={usersLoading}
            >
              {usersLoading ? "조회 중..." : "새로고침"}
            </button>
          </div>

          {usersLoading ? (
            <div className="loading-message">유저 목록을 불러오는 중...</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>사용자명</th>
                    <th>이름</th>
                    <th>이메일</th>
                    <th>새 패스워드</th>
                    <th>패스워드 확인</th>
                    <th>변경</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.name || "-"}</td>
                      <td>{user.email || "-"}</td>
                      <td>
                        <input
                          type="password"
                          placeholder="새 패스워드"
                          value={newPasswords[user.id] || ""}
                          onChange={(e) =>
                            setNewPasswords((prev) => ({
                              ...prev,
                              [user.id]: e.target.value,
                            }))
                          }
                          disabled={passwordChanging[user.id]}
                        />
                      </td>
                      <td>
                        <input
                          type="password"
                          placeholder="패스워드 확인"
                          value={confirmPasswords[user.id] || ""}
                          onChange={(e) =>
                            setConfirmPasswords((prev) => ({
                              ...prev,
                              [user.id]: e.target.value,
                            }))
                          }
                          disabled={passwordChanging[user.id]}
                        />
                      </td>
                      <td>
                        <button
                          className="change-password-button"
                          onClick={() => handlePasswordChange(user.id)}
                          disabled={
                            passwordChanging[user.id] ||
                            !newPasswords[user.id] ||
                            !confirmPasswords[user.id]
                          }
                        >
                          {passwordChanging[user.id] ? "변경 중..." : "변경"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length === 0 && (
                <div className="no-users-message">조회된 유저가 없습니다.</div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "delete" && (
        <div className="data-delete-section">
          <div className="delete-header">
            <h3>데이터 삭제</h3>
            <p className="delete-warning">
              ⚠️ <strong>주의:</strong> 삭제된 데이터는 복구할 수 없습니다.
              신중하게 진행해주세요. 만약 완전히 삭제된 데이터를 복구하고
              싶다면, 관리자에게 문의를 해주세요!
            </p>
          </div>

          <div className="delete-options">
            <div className="delete-card danger">
              <h4>특정 데이터 삭제</h4>
              <p>hos_id와 data_id를 입력하여 특정 데이터를 삭제합니다.</p>

              <div className="delete-form">
                <div className="input-row">
                  <div className="input-group">
                    <label>hos_id:</label>
                    <input
                      type="text"
                      placeholder="hos_id 입력"
                      className="delete-input"
                      value={deleteData.hosId}
                      onChange={(e) =>
                        setDeleteData((prev) => ({
                          ...prev,
                          hosId: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label>data_id:</label>
                    <input
                      type="text"
                      placeholder="data_id 입력"
                      className="delete-input"
                      value={deleteData.dataId}
                      onChange={(e) =>
                        setDeleteData((prev) => ({
                          ...prev,
                          dataId: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="confirmation-row">
                  <div className="input-group">
                    <label>확인 텍스트:</label>
                    <input
                      type="text"
                      placeholder="'delete'를 입력하세요"
                      className="delete-input confirmation-input"
                      value={deleteData.confirmText}
                      onChange={(e) =>
                        setDeleteData((prev) => ({
                          ...prev,
                          confirmText: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <button
                  className="delete-button single"
                  disabled={
                    !deleteData.hosId ||
                    !deleteData.dataId ||
                    deleteData.confirmText !== "delete" ||
                    deletionInProgress
                  }
                  onClick={handleDeleteData}
                >
                  {deletionInProgress
                    ? "예약 처리 중..."
                    : "24시간 후 삭제 예약"}
                </button>
              </div>
            </div>
          </div>

          {/* 예약된 삭제 목록 */}
          <div className="pending-deletions-section">
            <div className="pending-deletions-card">
              <h4>예약된 삭제 목록</h4>
              <p>24시간 후 삭제 예정인 데이터들을 관리할 수 있습니다.</p>

              <div className="refresh-section">
                <button
                  className="refresh-button"
                  onClick={fetchScheduledDeletions}
                >
                  목록 새로고침
                </button>
              </div>

              {scheduledDeletions.filter(
                (schedule) => schedule.status === "SCHEDULED"
              ).length === 0 ? (
                <div className="no-pending-deletions">
                  <div className="empty-state">
                    <div className="empty-icon">📅</div>
                    <p>현재 예약된 삭제 작업이 없습니다.</p>
                  </div>
                </div>
              ) : (
                <div className="pending-deletions-list">
                  {scheduledDeletions
                    .filter((schedule) => schedule.status === "SCHEDULED")
                    .map((schedule) => {
                      const scheduledTime = new Date(schedule.scheduled_for);
                      const now = new Date();
                      const timeRemaining = scheduledTime - now;
                      const hoursRemaining = Math.max(
                        0,
                        Math.floor(timeRemaining / (1000 * 60 * 60))
                      );
                      const minutesRemaining = Math.max(
                        0,
                        Math.floor(
                          (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
                        )
                      );

                      return (
                        <div
                          key={schedule.id}
                          className={`deletion-item scheduled ${schedule.status.toLowerCase()}`}
                        >
                          <div className="deletion-info">
                            <div className="deletion-ids">
                              <span className="label">hos_id:</span>
                              <span className="value">{schedule.hos_id}</span>
                              <span className="separator">|</span>
                              <span className="label">data_id:</span>
                              <span className="value">{schedule.data_id}</span>
                            </div>
                            <div className="deletion-meta">
                              <span className="schedule-time">
                                예약:{" "}
                                {new Date(schedule.created_at).toLocaleString(
                                  "ko-KR",
                                  {
                                    timeZone: "Asia/Seoul",
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  }
                                )}
                              </span>
                              <span
                                className={`status status-${schedule.status.toLowerCase()}`}
                              >
                                {schedule.status === "SCHEDULED"
                                  ? "예약됨"
                                  : schedule.status === "CANCELLED"
                                  ? "취소됨"
                                  : schedule.status === "EXECUTED"
                                  ? "실행됨"
                                  : schedule.status}
                              </span>
                            </div>
                            <div className="scheduled-time-info">
                              <span className="execution-time">
                                삭제 예정:{" "}
                                {scheduledTime.toLocaleString("ko-KR", {
                                  timeZone: "Asia/Seoul",
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  weekday: "short",
                                })}
                              </span>
                              {schedule.status === "SCHEDULED" &&
                                timeRemaining > 0 && (
                                  <span className="time-remaining">
                                    남은 시간: {hoursRemaining}시간{" "}
                                    {minutesRemaining}분
                                  </span>
                                )}
                              {schedule.status === "SCHEDULED" &&
                                timeRemaining <= 0 && (
                                  <span className="time-remaining overdue">
                                    삭제 대기 중 (처리 예정)
                                  </span>
                                )}
                            </div>
                          </div>

                          <div className="deletion-actions">
                            {schedule.status === "SCHEDULED" && (
                              <button
                                className="cancel-button"
                                onClick={() =>
                                  handleCancelScheduledDeletion(
                                    schedule.id,
                                    schedule.hos_id,
                                    schedule.data_id
                                  )
                                }
                              >
                                예약 취소하기
                              </button>
                            )}
                            {schedule.status === "EXECUTED" && (
                              <div className="completion-message">
                                ✅ 삭제 완료
                              </div>
                            )}
                            {schedule.status === "CANCELLED" && (
                              <div className="cancellation-message">
                                ⚠️ 예약 취소됨
                              </div>
                            )}
                            {schedule.status === "FAILED" && (
                              <div className="error-message">❌ 삭제 실패</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VIP11DBmanage;
